import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";

const baseURL = process.env.REACT_APP_BASEURL;

export const getInvoiceList = createAsyncThunk(
  "invoice/getInvoiceList",
  async ({ businessId, page = 1, limit = 10, status }, { rejectWithValue }) => {
    try {
      let queryUrl = baseURL + `/invoice`;
      let queryParams = [];
      if (businessId) queryParams.push(`business=${businessId}`);
      if (page) queryParams.push(`page=${page}`);
      if (limit) queryParams.push(`limit=${limit}`);
      if (status && status !== 'All') queryParams.push(`status=${status}`);
      
      if (queryParams.length > 0) {
        queryUrl += `?` + queryParams.join('&');
      }

      const response = await AxiosService.AxiostRequest({
        url: queryUrl,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch invoices");
    }
  }
);

export const getInvoiceAnalytics = createAsyncThunk(
  "invoice/getInvoiceAnalytics",
  async ({ businessId, range = 'week' }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        url: baseURL + `/analytics/invoice-system/${businessId}?range=${range}`,
        method: "get",
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch invoice analytics");
    }
  }
);

export const addInvoice = createAsyncThunk(
  "invoice/addInvoice",
  async (values, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/invoice`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to add invoice");
    }
  }
);
export const getIndividualnvoice = createAsyncThunk(
  "invoice/getIndividualnvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "get",
        url: baseURL + `/invoice/${id}`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch invoice details");
    }
  }
);
export const updateInvoice = createAsyncThunk(
  "invoice/updateInvoice",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "put",
        url: baseURL + `/invoice/${id}`,
        data: values,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to update invoice");
    }
  }
);
export const resendInvoice = createAsyncThunk(
  "invoice/resendInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "post",
        url: baseURL + `/invoice/resend/${id}`,
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to resend invoice");
    }
  }
);
export const payInvoiceManually = createAsyncThunk(
  "invoice/payInvoiceManually",
  async ({ id, note }, { rejectWithValue }) => {
    try {
      const response = await AxiosService.AxiostRequest({
        method: "patch",
        url: baseURL + `/invoice/${id}/pay-manually`,
        data: { note },
      });
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to mark invoice as paid");
    }
  }
);
export const deleteInvoice = createAsyncThunk(
  "invoice/deleteInvoice",
  async (id, { rejectWithValue }) => {
    try {
      await AxiosService.AxiostRequest({
        method: "delete",
        url: baseURL + `/invoice/${id}`,
      });
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete invoice");
    }
  }
);
const invoiceSlice = createSlice({
  name: "invoice",
  initialState: {
    invoiceList: [],
    loading: false,
    pagination: null,
    individualInvoice: null,
    analytics: {
      dashboardStats: null,
      graphData: null,
      invoiceStats: null
    }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getInvoiceList.fulfilled, (state, action) => {
      state.invoiceList = action?.payload?.list || [];
      state.pagination = action?.payload?.pagination || null;
      state.loading = false;
    });
    builder.addCase(getInvoiceAnalytics.fulfilled, (state, action) => {
      state.analytics = action.payload;
      state.loading = false;
    });
    builder.addCase(addInvoice.fulfilled, (state, action) => {
      state.invoiceList.unshift(action.payload);
      state.loading = false;
      toast.success("Invoice added successfully");
    });
    builder.addCase(getIndividualnvoice.fulfilled, (state, { payload }) => {
      state.individualInvoice = payload;
      state.loading = false;
    });
    builder.addCase(deleteInvoice.fulfilled, (state, { payload }) => {
      state.invoiceList = state?.invoiceList?.filter(
        (invoice) => invoice._id !== payload
      );
      state.loading = false;
      toast.success("Invoice deleted successfully");
    });
    builder.addCase(updateInvoice.fulfilled, (state, action) => {
      state.loading = false;
      state.individualInvoice = action.payload;
      toast.success("Invoice updated successfully");
    });
    builder.addCase(resendInvoice.fulfilled, (state) => {
      state.loading = false;
      toast.success("Invoice resent successfully");
    });
    builder.addCase(payInvoiceManually.fulfilled, (state, action) => {
      state.loading = false;
      state.individualInvoice = action.payload;
      toast.success("Invoice marked as paid");
    });

    // General Matchers
    builder.addMatcher(
      (action) => action.type.startsWith("invoice/") && action.type.endsWith("/pending"),
      (state) => { state.loading = true; }
    );
    builder.addMatcher(
      (action) => action.type.startsWith("invoice/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
      (state, action) => {
        state.loading = false;
        if (action.type.endsWith("/rejected")) {
          toast.error(action.payload?.message || "Something went wrong");
        }
      }
    );
  },
});
export default invoiceSlice.reducer;
